import { CustomerSchema, LeadSchema } from '../CustomerFormProvider.tsx';
import { GridLayoutContainer, GridLayoutItem, Typography } from '~/index.ts';
import HookFormSelect, {
  HookFormSelectProps,
} from '~/components/atoms/HookForm/Select/HookFormSelect.tsx';
import { CrmType } from '@upstart/harmony/lib/constants.js';
import { ReactNode } from 'react';
import { CustomerSectionProps } from '../CustomerForm.tsx';
import { getDealMapping } from '../utils/formHelper.ts';
import { CustomerFormLayoutOverrides } from '../CustomerForm.tsx';

export type Map = {
  id: string | number;
  name: string;
};

type Mapping = Map[];

export type CustomerFormSourceMapping = {
  mapping?: { [key: string]: Mapping };
  secondary?: Mapping;
  trackingCode?: Mapping;
};

export interface LeadSectionProps extends CustomerSectionProps {
  sourceMapping?: CustomerFormSourceMapping;
  dealerCrmType?: CrmType;
  currentLead?: LeadSchema;
  layoutOverrides?: CustomerFormLayoutOverrides;
}

const LeadSection: React.FC<LeadSectionProps> = ({
  currentLead,
  control,
  formTexts,
  layoutOverrides,
  sourceMapping,
  dealerCrmType,
  formMode,
  disabled,
}) => {
  const renderEditFields = (): ReactNode => {
    const currentLeadType = currentLead?.type;
    const leadTypeOptions = sourceMapping
      ? Object.keys(sourceMapping.mapping ?? {}).map((type) => ({
          id: type,
          name: type,
        }))
      : [];

    const sourceOptions =
      currentLeadType && sourceMapping && sourceMapping.mapping
        ? sourceMapping.mapping[currentLeadType]
        : [];

    const fieldDefinitions: Array<
      Omit<HookFormSelectProps<CustomerSchema>, 'control'> & {
        flexPercentage: number;
      }
    > = [
      {
        name: 'lead.type',
        source: leadTypeOptions,
        flexPercentage: 50,
        placeholder: formTexts.lead.type,
        dataKey: 'id',
        dataValue: 'name',
      },
      {
        name: 'lead.sourceId',
        source: sourceOptions,
        flexPercentage: 50,
        disabled: !currentLeadType,
        placeholder: formTexts.lead.source,
        dataKey: 'id',
        dataValue: 'name',
      },
    ];

    if (dealerCrmType === CrmType.DEALERSOCKET) {
      fieldDefinitions.push({
        name: 'lead.secondaryId',
        source: sourceMapping?.secondary ?? [],
        flexPercentage: 50,
        placeholder: formTexts.lead.secondary,
        dataKey: 'id',
        dataValue: 'name',
      });

      fieldDefinitions.push({
        name: 'lead.trackingCode',
        source: sourceMapping?.trackingCode ?? [],
        flexPercentage: 50,
        placeholder: formTexts.lead.trackingCode,
        dataKey: 'id',
        dataValue: 'name',
      });
    }

    return fieldDefinitions.map((fieldDef) => {
      return (
        <GridLayoutItem
          key={fieldDef.name}
          flexPercentage={fieldDef.flexPercentage}
        >
          <HookFormSelect
            fullWidth
            control={control}
            name={fieldDef.name}
            placeholder={fieldDef.placeholder}
            source={fieldDef.source}
            disabled={fieldDef.disabled || disabled}
            dataKey={fieldDef.dataKey}
            dataValue={fieldDef.dataValue}
            size={layoutOverrides?.select?.size}
          />
        </GridLayoutItem>
      );
    });
  };

  const renderPreviewFields = (): ReactNode => {
    const { sourceMap, secondaryMap, trackingMap } = getDealMapping(
      sourceMapping ?? {},
      currentLead,
    );

    return (
      <>
        <GridLayoutItem>
          <Typography
            styleOverrides={{ color: 'text.secondary' }}
          >{`Type: ${currentLead?.type}`}</Typography>
        </GridLayoutItem>
        {sourceMap && (
          <GridLayoutItem>
            <Typography
              styleOverrides={{ color: 'text.secondary' }}
            >{`Source: ${sourceMap?.name}`}</Typography>
          </GridLayoutItem>
        )}
        {secondaryMap && (
          <GridLayoutItem>
            <Typography
              styleOverrides={{ color: 'text.secondary' }}
            >{`Opportunity: ${secondaryMap?.name}`}</Typography>
          </GridLayoutItem>
        )}
        {trackingMap && (
          <GridLayoutItem>
            <Typography
              styleOverrides={{ color: 'text.secondary' }}
            >{`Tracking: ${trackingMap?.name}`}</Typography>
          </GridLayoutItem>
        )}
      </>
    );
  };

  const showEditFields = !layoutOverrides?.lockLeads && formMode === 'add';
  const spacing = showEditFields ? 3 : 1;
  const sectionContent = showEditFields
    ? renderEditFields()
    : renderPreviewFields();
  return (
    <GridLayoutContainer spacing={spacing}>
      {sectionContent}
    </GridLayoutContainer>
  );
};

export default LeadSection;
